import { Mail, MailX, Plus, Trash2, Undo2 } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Field, FormError } from '@/components/ui/field';
import { StatusBadge } from '@/components/ui/status-badge';
import { useToast } from '@/lib/toast-context';
import {
  useAddRecipient,
  useLiftSuppression,
  useNotificationRecipients,
  useRemoveRecipient,
  useSuppressions,
  useUpdateRecipient,
} from '@/hooks/email-settings';
import { ApiError } from '@/lib/api';
import { suppressionReasonLabel, EMAIL_SUPPRESSION_REASON_HINTS } from '@shared/schemas/email';
import { ADMIN_NOTIFICATION_SUMMARY } from '@shared/schemas/notification-email';
import type { NotificationRecipientSummary } from '@shared/schemas/notification-email';

/*
 * /admin/notification-emails — ROADMAP.md §11.3, **moved into phase 8**.
 *
 * It moved because §8.6 and §8.7 both send "a notification to every
 * `notification_email_recipient`", that table is seeded empty on purpose, and
 * §8.7's exit criterion is that a real application notifies the right
 * addresses. Leaving the only screen that can put a row there five phases
 * downstream would have made the criterion unmeetable — and left the flag
 * standing since phase 5 that nobody is emailed when a client asks for a clean.
 *
 * **The prototype's own copy for this screen was not captured** — MANIFEST.md
 * says screenshots of the 18 admin pages need a login no conversation has. So
 * the explanatory text here is written rather than ported, which is the one
 * place in phase 8 that is true. It says what the list does and what happens
 * when it is empty, because an empty list that looks fine is exactly how nobody
 * finds out the office is not being told anything.
 *
 * The suppression list shares the page (§8.2). They are two halves of one
 * question — who gets our mail, and who does not — and a page holding one table
 * is a page nobody finds.
 */

export function NotificationEmailsPage() {
  return (
    <AdminPage
      title="Notification Emails"
      description="Who gets told when something happens, and which addresses we have stopped writing to."
    >
      <div className="space-y-8">
        <RecipientsCard />
        <SuppressionsCard />
      </div>
    </AdminPage>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-ink-300 bg-white shadow-card">{children}</section>
  );
}

function CardHeader({ title, hint }: { title: string; hint: React.ReactNode }) {
  return (
    <header className="border-b border-ink-300 px-5 py-4">
      <h2 className="text-h2">{title}</h2>
      <p className="mt-1 text-body text-ink-700">{hint}</p>
    </header>
  );
}

function RecipientsCard() {
  const toast = useToast();
  const recipients = useNotificationRecipients();
  const add = useAddRecipient();
  const update = useUpdateRecipient();
  const remove = useRemoveRecipient();

  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<NotificationRecipientSummary | null>(null);

  const items = recipients.data?.items ?? [];
  const active = items.filter((row) => row.isActive);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    add.mutate(
      { email, label: label.trim() === '' ? undefined : label.trim() },
      {
        onSuccess: () => {
          setEmail('');
          setLabel('');
          toast.success('Added. They will be told from the next notification on.');
        },
        onError: (cause) => {
          setError(
            cause instanceof ApiError
              ? (cause.fieldErrors.email ?? cause.message)
              : 'That could not be saved.',
          );
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader
        title="Who gets told"
        hint="Every address here is emailed when any of these happen. In-app notifications go to every admin regardless — this list is for people who need the email too."
      />

      <div className="px-5 py-4">
        <ul className="grid gap-1 text-body text-ink-700 sm:grid-cols-2">
          {ADMIN_NOTIFICATION_SUMMARY.map((line) => (
            <li key={line} className="flex gap-2">
              <span aria-hidden className="text-bark-600">
                ·
              </span>
              {line}
            </li>
          ))}
        </ul>
      </div>

      {/*
        **The warning that matters.** An empty list is not an empty state — it
        is mail nobody is receiving, and the failure is completely silent:
        everything looks fine, the requests are all in the queue, and nobody
        outside the app knows a customer got in touch.
      */}
      {!recipients.isPending && active.length === 0 ? (
        <div className="mx-5 mb-4 rounded-md border border-status-priority-text/30 bg-red-50 px-4 py-3">
          <p className="text-body text-status-priority-text">
            <strong>Nobody is being emailed.</strong> Requests, quotes and applications are still
            being stored and still show in the app — but no one is told outside it until an address
            is added here.
          </p>
        </div>
      ) : null}

      <div className="border-t border-ink-300">
        {recipients.isPending ? (
          <p className="px-5 py-8 text-center text-body text-ink-500">Loading…</p>
        ) : recipients.isError ? (
          // A failed request is an error, never an empty state (P22). "No
          // recipients" here would read as "nobody is configured", which is a
          // completely different and much more alarming fact.
          <ErrorState
            title="That list could not be loaded"
            onRetry={() => void recipients.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="No addresses yet."
            hint="Add whoever should hear about a new request, quote or application."
          />
        ) : (
          <ul className="divide-y divide-ink-300">
            {items.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="break-all text-body text-ink-900">{row.email}</p>
                  {row.label ? (
                    <p className="text-meta text-ink-500">{row.label}</p>
                  ) : (
                    // design-system.md §7 — never a bare em-dash where a value
                    // would go. Say what is absent and why it matters.
                    <p className="text-meta italic text-ink-500">
                      No label — add one so the list says who this is
                    </p>
                  )}
                </div>

                {row.isActive ? null : <StatusBadge label="Switched off" tone="cancelled" />}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    update.mutate(
                      { id: row.id, input: { isActive: !row.isActive } },
                      {
                        onSuccess: () => {
                          toast.success(
                            row.isActive
                              ? `${row.email} will no longer be emailed.`
                              : `${row.email} will be emailed again.`,
                          );
                        },
                      },
                    );
                  }}
                >
                  {row.isActive ? 'Switch off' : 'Switch on'}
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${row.email}`}
                  onClick={() => {
                    setRemoving(row);
                  }}
                >
                  <Trash2 aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={submit} className="space-y-4 border-t border-ink-300 px-5 py-4">
        <FormError>{error}</FormError>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Email address"
            name="email"
            type="email"
            required
            placeholder="office@honeycombcleaning.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <Field
            label="Who is it"
            name="label"
            placeholder="Owner, Office…"
            hint="Optional, and worth filling in — a list of bare addresses tells nobody whether one is missing."
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
            }}
          />
        </div>

        <Button type="submit" disabled={add.isPending}>
          <Plus aria-hidden />
          {add.isPending ? 'Adding…' : 'Add address'}
        </Button>
      </form>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title={`Remove ${removing?.email ?? ''}?`}
        description="They will stop receiving these emails. If you only want to pause it, use Switch off instead — that keeps the address here."
        confirmLabel="Remove the address"
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          if (!removing) return;
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Removed.');
              setRemoving(null);
            },
          });
        }}
      />
    </Card>
  );
}

function SuppressionsCard() {
  const toast = useToast();
  const suppressions = useSuppressions();
  const lift = useLiftSuppression();

  const items = suppressions.data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title="Addresses we have stopped writing to"
        hint="An address lands here when mail to it fails permanently or somebody reports it as spam. We stop sending, because continuing to write to a dead mailbox is what gets the rest of our mail treated as junk."
      />

      <div>
        {suppressions.isPending ? (
          <p className="px-5 py-8 text-center text-body text-ink-500">Loading…</p>
        ) : suppressions.isError ? (
          <ErrorState onRetry={() => void suppressions.refetch()} />
        ) : items.length === 0 ? (
          // The good state, and it says so — an empty list here is not a
          // problem to fix.
          <EmptyState
            icon={MailX}
            title="Nothing is blocked."
            hint="Every address we have written to is still accepting mail."
          />
        ) : (
          <ul className="divide-y divide-ink-300">
            {items.map((row) => (
              <li key={row.email} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="break-all text-body text-ink-900">{row.email}</p>
                  <p className="text-meta text-ink-500">
                    {suppressionReasonLabel(row.reason)} —{' '}
                    {EMAIL_SUPPRESSION_REASON_HINTS[row.reason] ?? ''}
                  </p>
                  {row.detail ? (
                    <p className="text-meta text-ink-500">
                      The server said: <code>{row.detail}</code>
                    </p>
                  ) : null}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={lift.isPending}
                  onClick={() => {
                    lift.mutate(row.email, {
                      onSuccess: () => {
                        toast.success(`We will write to ${row.email} again.`);
                      },
                      onError: () => {
                        toast.error('That could not be lifted.');
                      },
                    });
                  }}
                >
                  <Undo2 aria-hidden />
                  Start writing again
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* People do fix their mailboxes — business-rules.md §8. Said on the
          screen, because otherwise "blocked" reads as permanent. */}
      <p className="border-t border-ink-300 px-5 py-3 text-meta text-ink-500">
        Lifting a block is recorded, along with who did it. It is worth doing if somebody tells you
        they have fixed their email — people do.
      </p>
    </Card>
  );
}
